/**
 * Binds in-room session matches (`roomMatches` UUID) to `liveMatches/{liveDocId}`.
 * Legacy rooms used `liveMatches/{roomCode}` before multi-match.
 */

/**
 * @param {string | null | undefined} roomMatchId
 * @param {string} roomCode
 * @returns {string}
 */
export function resolveLiveDocumentId(roomMatchId, roomCode) {
  const rm = String(roomMatchId || "").trim();
  if (rm) return rm;
  return String(roomCode || "").trim();
}

/**
 * @param {string} liveDocId
 * @param {string} roomCode
 */
export function isLegacyRoomLiveDocument(liveDocId, roomCode) {
  return Boolean(roomCode) && liveDocId === roomCode;
}

/**
 * Live doc finished before `roomMatches` status catches up (multi-ref sync).
 * Also treats idle+winner (doc cleared after finish) as ended so Play is not blocked.
 * @param {import('./roomGovernanceTypes').RoomSessionMatch | null | undefined} roomMatch
 * @param {Record<string, { status?: string; winner?: string | null }>} [liveById]
 */
export function isRoomMatchEndedOnLiveDoc(roomMatch, liveById) {
  if (!roomMatch?.id) return false;
  const live = liveById?.[roomMatch.id];
  if (!live) return false;
  if (live.status === "finished" && Boolean(live.winner)) return true;
  if (live.status === "idle" && Boolean(live.winner)) return true;
  return false;
}

/**
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 */
export function listOngoingRoomSessionMatches(roomMatches) {
  return (roomMatches || [])
    .filter((m) => m && (m.status === "ready" || m.status === "live"))
    .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
}

/**
 * Ongoing rows for hub/roster — excludes matches whose live doc already finished.
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 * @param {Record<string, { status?: string; winner?: string | null }>} [liveById]
 */
export function listEffectiveOngoingRoomSessionMatches(roomMatches, liveById) {
  return listOngoingRoomSessionMatches(roomMatches).filter(
    (m) => !isRoomMatchEndedOnLiveDoc(m, liveById)
  );
}

/**
 * @param {string | null | undefined} uid
 * @param {import('./roomGovernanceTypes').RoomSessionMatch | null | undefined} roomMatch
 */
export function canManageRoomSessionMatch(uid, roomMatch) {
  if (!uid || !roomMatch) return false;
  return roomMatch.managedBy === uid;
}

/**
 * Default room match to open on Match tab after restore.
 * @param {string | null | undefined} uid
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 */
export function pickDefaultFocusedRoomMatchId(uid, roomMatches, liveById = null) {
  const ongoing = liveById
    ? listEffectiveOngoingRoomSessionMatches(roomMatches, liveById)
    : listOngoingRoomSessionMatches(roomMatches);
  if (!ongoing.length) return null;
  const mine = ongoing.find((m) => m.managedBy === uid);
  if (mine) return mine.id;
  return ongoing[0].id;
}
